import React, { forwardRef, FocusEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useField, useFormikContext, FieldInputProps } from 'formik'
import { TextInputContainer } from './style'
import { getStringLength } from 'components/utils/stringCheck'
import { InputCharacterCounter, Size } from '..'
import IconButton from 'particles/Button/IconButton'
import Tooltip from 'particles/Tooltip'
import Label, { LabelSize, LinkConfig } from 'particles/Label'
import Text from 'particles/Text'
import Icon, { IconName } from 'particles/Icon'
import CommentContainer from 'components/Shared/Comment'
import { ReadOnly } from '..'

export const sizeMap: Record<Size, { padding: number; lineHeight: number }> = {
    m: {
        padding: 22,
        lineHeight: 20,
    },
    s: {
        padding: 14,
        lineHeight: 20,
    },
}

// omit `name` (required), and make it into `name` (optional) for use case in rich info list component
// since, in rich info list component must not have required name field, since it already handled in parent formik wrapper
export interface TextFieldProps extends Omit<HTMLInputElement | FieldInputProps<string>, 'size' | 'name'> {
    className?: string
    label?: string
    name?: string
    showErrorStyle?: boolean
    subtext?: string
    size?: Size
    readOnlyEmptyText?: string
    isRequired?: boolean
    showInputLength?: boolean
    maxInputLength?: number
    placeholder?: string
    width?: number | string
    minWidth?: string
    isDisabled?: boolean
    tooltipContext?: { title?: string; label?: string }
    error?: string
    isReadOnly?: boolean
    onChange?: (value: string) => void
    isCommentable?: boolean
    isCommented?: boolean
    onLabelClick?: () => void
    value: string
    inputIcon?: IconName
    identifier?: string
    showDelete?: boolean
    onDelete?: React.MouseEventHandler<HTMLButtonElement>
    autoComplete?: 'on' | 'off'
    labelSize?: LabelSize
    link?: LinkConfig
    labelTooltip?: string
    type?: 'text' | 'password' | 'email'
    enableTrim?: boolean
    onBlur?: (e: FocusEvent<HTMLInputElement>) => void
    onFocus?: (e: FocusEvent<HTMLInputElement>) => void
    dataTestId?: string
}

const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
    (
        {
            className,
            label = '',
            subtext,
            size = 'm',
            readOnlyEmptyText,
            width,
            minWidth = '198px',
            isDisabled = false,
            isRequired = false,
            showErrorStyle = false,
            showInputLength = false,
            placeholder,
            maxInputLength = 100,
            tooltipContext = { title: '', label: '' },
            error,
            isReadOnly = false,
            isCommentable = false,
            isCommented = false,
            onChange,
            onLabelClick,
            value,
            inputIcon,
            identifier,
            showDelete = false,
            onDelete,
            autoComplete = 'on',
            labelSize,
            link,
            labelTooltip,
            type = 'text',
            enableTrim = false,
            onBlur,
            dataTestId,
            onFocus,
            ...props
        },
        ref,
    ) => {
        const [t] = useTranslation()
        return (
            <TextInputContainer
                width={width}
                minWidth={minWidth}
                className={`${className} text-input-container`}
                hasError={showErrorStyle || Boolean(error)}
                size={size}
            >
                {(label || subtext) && (
                    <CommentContainer identifier={identifier}>
                        <Label
                            title={label || ''}
                            subtext={subtext}
                            isRequired={isRequired}
                            size={labelSize ? labelSize : size}
                            isCommentable={isCommentable}
                            isCommented={isCommented}
                            onClick={onLabelClick}
                            link={link}
                            tooltip={labelTooltip}
                        />
                    </CommentContainer>
                )}
                {isReadOnly ? (
                    <ReadOnly
                        icon={inputIcon}
                        width={width}
                        size={size}
                        value={
                            value ? value : readOnlyEmptyText ? readOnlyEmptyText : t('placeholderText.noDetailsAdded')
                        }
                    />
                ) : (
                    <div className={'input-count-container'}>
                        <div className={`textfield-input-container ${isDisabled ? 'disabled' : ''}`}>
                            {inputIcon && (
                                <div className="icon-container">
                                    <Icon
                                        name={inputIcon}
                                        size="20px"
                                        color={isDisabled ? 'placeholder' : 'primary'}
                                        className="input-icon"
                                    />
                                </div>
                            )}
                            <input
                                {...props}
                                data-testid={dataTestId}
                                autoComplete={autoComplete}
                                value={Boolean(value) ? value : ''}
                                ref={ref}
                                required={isRequired}
                                placeholder={placeholder}
                                disabled={isDisabled}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    if (onChange) {
                                        const value = enableTrim ? e.target.value.trim() : e.target.value
                                        onChange(value)
                                    }
                                }}
                                type={type}
                                onBlur={onBlur}
                                onFocus={onFocus}
                                data-emoji-input="unicode"
                            />
                            {isDisabled && (tooltipContext?.title || tooltipContext?.label) && (
                                <div className="icon-container">
                                    <Tooltip
                                        placement="bottom"
                                        title={tooltipContext?.title}
                                        label={tooltipContext?.label}
                                    >
                                        <IconButton
                                            icon="question"
                                            size="xs"
                                            form="icon"
                                            color="neutral"
                                            className="tooltip-icon"
                                        />
                                    </Tooltip>
                                </div>
                            )}
                            {showDelete && onDelete && (
                                <div className="icon-container">
                                    <IconButton
                                        icon="x"
                                        form="icon"
                                        onClick={onDelete}
                                        size="xs"
                                        color="neutral"
                                        isDisabled={isDisabled}
                                    />
                                </div>
                            )}
                        </div>
                        {(error || showInputLength) && (
                            <div className="counter-and-error">
                                <Text
                                    data-testid={`${dataTestId}_error`}
                                    scale="caption400"
                                    color="error"
                                    className="error"
                                    as="div"
                                >
                                    {error}
                                </Text>
                                {showInputLength && (
                                    <InputCharacterCounter
                                        inputCharLength={getStringLength(value)}
                                        maxInputLength={maxInputLength}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                )}
            </TextInputContainer>
        )
    },
)

TextField.displayName = 'TextField'

export default TextField

export interface FormikTextFieldProps extends Omit<TextFieldProps, 'value' | 'onChange'> {
    value?: string
    name: string
    isError?: boolean
    onChange?: (value: string) => void
    showError?: boolean // set false if want to use external error state to control the error display
    validateByTouch?: boolean // set to false if you want to use an external error state to control error display. It will show errors without requiring the field to be touched.
    ref?: React.RefObject<HTMLInputElement>
    enableTrim?: boolean
    matchWith?: RegExp // pattern that only allow user input to the field
}

const FormikTextField: React.FC<FormikTextFieldProps> = ({
    onChange,
    enableTrim,
    onBlur,
    showError = true,
    validateByTouch = true,
    autoComplete,
    matchWith,
    ...props
}) => {
    const [field, meta] = useField(props.name)
    const { setFieldValue, setFieldTouched } = useFormikContext()

    const hasError = (showError && (validateByTouch ? meta.touched && !!meta.error : !!meta.error)) || props.isError

    const handleChange = (value: string) => {
        let newValue = value

        // filter value with pattern if provided
        if (matchWith) {
            newValue = value.replace(matchWith, '')
        }
        if (onChange) {
            onChange(value)
            if (!meta.touched) setFieldTouched(field.name as never, true, false)
            return
        }
        setFieldValue(field.name as never, newValue)
    }

    return (
        <TextField
            {...props}
            value={field?.value}
            error={(hasError && meta.error) || ''}
            onChange={handleChange}
            enableTrim={enableTrim}
            onBlur={onBlur}
            autoComplete={autoComplete}
        />
    )
}
export { FormikTextField }
